/**
 * GST Helper utilities for frontend tax calculation
 */

// GST State Codes mapping
export const GST_STATE_CODES: Record<string, string> = {
  '01': 'Jammu & Kashmir',
  '02': 'Himachal Pradesh',
  '03': 'Punjab',
  '04': 'Chandigarh',
  '05': 'Uttarakhand',
  '06': 'Haryana',
  '07': 'Delhi',
  '08': 'Rajasthan',
  '09': 'Uttar Pradesh',
  '10': 'Bihar',
  '11': 'Sikkim',
  '12': 'Arunachal Pradesh',
  '13': 'Nagaland',
  '14': 'Manipur',
  '15': 'Mizoram',
  '16': 'Tripura',
  '17': 'Meghalaya',
  '18': 'Assam',
  '19': 'West Bengal',
  '20': 'Jharkhand',
  '21': 'Odisha',
  '22': 'Chhattisgarh',
  '23': 'Madhya Pradesh',
  '24': 'Gujarat',
  '26': 'Dadra and Nagar Haveli and Daman and Diu',
  '27': 'Maharashtra',
  '29': 'Karnataka',
  '30': 'Goa',
  '31': 'Lakshadweep',
  '32': 'Kerala',
  '33': 'Tamil Nadu',
  '34': 'Puducherry',
  '35': 'Andaman & Nicobar Islands',
  '36': 'Telangana',
  '37': 'Andhra Pradesh (New)',
  '38': 'Ladakh',
  '97': 'Other Territory',
  '99': 'Center Jurisdiction',
};

/**
 * Extract state code from GSTIN
 * GSTIN format: 07AAQCM7695A1Z1
 * First 2 digits represent the state code
 */
export function extractStateCodeFromGSTIN(gstin: string): string | null {
  if (!gstin || gstin.length < 2) {
    return null;
  }
  return gstin.substring(0, 2);
}

/**
 * Get state name from GSTIN
 */
export function getStateFromGSTIN(gstin: string): string | null {
  const stateCode = extractStateCodeFromGSTIN(gstin);
  if (!stateCode) {
    return null;
  }
  return GST_STATE_CODES[stateCode] || null;
}

/**
 * Determine tax type based on company and customer GSTIN
 * If same state: CGST + SGST (Intra-state)
 * If different state: IGST (Inter-state)
 */
export function determineTaxType(
  companyGSTIN: string,
  customerGSTIN: string
): 'IGST' | 'CGST_SGST' {
  const companyStateCode = extractStateCodeFromGSTIN(companyGSTIN);
  const customerStateCode = extractStateCodeFromGSTIN(customerGSTIN);

  // If we can't extract state codes, default to IGST
  if (!companyStateCode || !customerStateCode) {
    return 'IGST';
  }

  // Compare state codes
  if (companyStateCode === customerStateCode) {
    // Same state - Intra-state transaction
    return 'CGST_SGST';
  } else {
    // Different state - Inter-state transaction
    return 'IGST';
  }
}

/**
 * Validate GSTIN format
 * Format: 2 digits (state code) + 10 alphanumeric + 1 letter + 1 digit + 1 alphanumeric
 */
export function isValidGSTIN(gstin: string): boolean {
  if (!gstin || gstin.length !== 15) {
    return false;
  }

  // Check if first 2 characters are digits (state code)
  const stateCode = gstin.substring(0, 2);
  if (!/^\d{2}$/.test(stateCode)) {
    return false;
  }

  // Check if state code exists in our mapping
  if (!GST_STATE_CODES[stateCode]) {
    return false;
  }

  // Basic GSTIN format validation
  // Format: 07AAQCM7695A1Z1
  const gstinRegex = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}\d{1}[A-Z\d]{1}$/;
  return gstinRegex.test(gstin);
}
